/**
 * MobileLayout — PRI-494 "Viewfinder" fixed 3-zone shell.
 *
 * Zone geometry (390×844 / iPhone 15):
 *   Header  : 44px fixed bar below safe-area-inset-top
 *   Camera  : min(56.25vw, 30vh) fixed below header — no ResizeObserver, deterministic CSS
 *   Results : fixed from camera-bottom to input-top, overflow-y:auto, internal scroll only
 *   Input   : auto-height, anchored ABOVE 49px MobileTabBar + safe-area (PRI-494
 *             2026-07-29: was a hard 68px box that overflowed over the tab bar;
 *             now grows upward and publishes its measured height via --pv2-input-h)
 *
 * iOS scar tissue preserved:
 *   - body scroll-lock (position:fixed, overflow:hidden) — prevents WebKit keyboard jitter
 *   - translateZ(0) + willChange:transform on camera zone — compositor layer (no flicker)
 *   - --pv2-vv-offset / --keyboard-offset CSS vars — driven by useVisualViewportKeyboard
 *   - WebkitOverflowScrolling:touch on results frame — smooth momentum scroll on iOS
 */
import type { Dispatch, RefObject, SetStateAction } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import TermsModal from '../TermsModal'
import ConversionDialog from '../ConversionDialog'
import UpgradeModal from '../UpgradeModal'
import MobileTabBar from '../MobileTabBar'
import MobileMorePanel from '../MobileMorePanel'
import MobileStreamingHUD from '../PrimateVision/MobileStreamingHUD'
import ErrorBoundary from '../../core/components/ErrorBoundary'
import { MobileCameraPreview } from '../../core/components/v2/MobileCameraPreview'
import {
  L,
  MOBILE_ZONES,
  mobileCameraHeightCss,
  type ResultPill,
  type VideoSource,
  type QuotaBarData,
  type CostPreviewData,
} from '../../core/components/v2/pv2-shared'
import { ProgressBlock, StreamProgressTray, StreamErrorTray } from '../../core/components/v2/V2StreamOverlay'
import { MobileSessionHistory, computeHistoryCount } from './MobileSessionHistory'
import { InputBar, VideoSourcePopover } from '../../core/components/v2/V2InputBar'
import { ResultPillComp } from '../../core/components/v2/V2ResultPill'
import { EmptyStateDemo } from '../../core/components/v2/EmptyStateDemo'
import { useStreamContext } from '../../contexts/StreamContext'
import { useInferenceContext } from '../../contexts/InferenceContext'
import { useVideoLibraryContext } from '../../contexts/VideoLibraryContext'
import { usePrimateAuth } from '../../primate/auth/usePrimateAuth'
import { trackStreamPromptUpdated, trackMobileNewResultChipTapped } from '../../core/telemetry/events'
import type { LiveInferenceResult } from '../../core/hooks/types'
import type { CompiledQuery, ParseState } from '../../core/components/v2/compiled-query-types'

export interface MobileLayoutProps {
  // Modal / overlay state
  showTermsForAction: boolean
  handleTermsAccepted: () => void
  showConversionDialog: boolean
  conversionFile: File | null
  handleConversionConfirm: (convertedFile: File) => void | Promise<void>
  handleConversionCancel: () => void
  showUpgradeForDownload: boolean
  setShowUpgradeForDownload: (v: boolean) => void
  // Mobile HUD / navigation
  showMobileHUD: boolean
  moreOpen: boolean
  setMoreOpen: Dispatch<SetStateAction<boolean>>
  setMobileHUDOpen: (v: boolean) => void
  // Display state
  showCarousel: boolean
  /** True once Clerk auth + initial fetches have resolved. Defers first paint of input bar. */
  authLoaded: boolean
  carouselDismissed: boolean
  setCarouselDismissed: (v: boolean) => void
  showProgress: boolean
  showStreamProgress: boolean
  streamProgressDismissing: boolean
  offlineProgressDismissing: boolean
  processingText: string
  processingStep: number
  jobProgress: { stage: string; pct: number; message: string } | null
  completingSteps: boolean
  offlineJobActive: boolean
  // Input / prompt
  prompt: string
  setPrompt: (v: string) => void
  compiledQuery?: CompiledQuery | null
  parseState?: ParseState
  queryError?: string | null
  onCompiledQueryChange?: (q: CompiledQuery) => void
  onEditModeToggle?: (open: boolean) => void
  videoMenuOpen: boolean
  setVideoMenuOpen: Dispatch<SetStateAction<boolean>>
  videoSource: VideoSource | null
  quotaBar: QuotaBarData | null
  /** PRI-488 F1: pre-Run cost preview (validate_only). */
  costPreview?: CostPreviewData | null
  /** PRI-488 F7: public video id (video_…) for the batch composer. */
  publicVideoId?: string | null
  canDownload: boolean
  downloadingPillIds: Set<string>
  inputCompact: boolean
  // Refs
  streamPillIdRef: RefObject<string | null>
  uploadingPillIdsRef: RefObject<Set<string>>
  threadAreaRef: RefObject<HTMLDivElement | null>
  threadBottomRef: RefObject<HTMLDivElement | null>
  inputBarContainerRef: RefObject<HTMLDivElement | null>
  fileInputRef: RefObject<HTMLInputElement | null>
  lastStreamResultRef: RefObject<LiveInferenceResult | null>
  // Handlers
  handleSubmit: () => void
  handleFileSelected: (f: File) => void
  handleDeleteResult: (pill: ResultPill) => void
  handleDownloadPill: (pill: ResultPill) => void
  handleRerunStream: (prompt: string) => void
  attachRenderedVideoForced: (pillId: string) => void
  attachStreamRecordingUrlForced: (pillId: string, recordingUrl: string) => void
  handleSelectLiveStreams: () => void
  handleSelectUserVideo: (videoId: string) => void
  handleSelectDemoVideo: (videoId: string) => void
  handleSignOut: () => void
  // Auth display (not in PrimateAuthContext)
  planTier: string
  userName: string | null
  userInitials: string
  // P3: session history overlay (optional — omit to suppress the history button entirely)
  /** When provided, renders a clock-icon button in the header. P3 MobileLayout wires this internally. */
  onOpenHistory?: () => void
  /** Badge count shown on the history icon. */
  historyCount?: number
}

export function MobileLayout({
  showTermsForAction, handleTermsAccepted,
  showConversionDialog, conversionFile, handleConversionConfirm, handleConversionCancel,
  showUpgradeForDownload, setShowUpgradeForDownload,
  showMobileHUD, moreOpen, setMoreOpen, setMobileHUDOpen,
  showCarousel, authLoaded, carouselDismissed, setCarouselDismissed,
  showProgress, showStreamProgress, streamProgressDismissing, offlineProgressDismissing,
  processingText, processingStep, jobProgress, completingSteps, offlineJobActive,
  prompt, setPrompt, compiledQuery, parseState, queryError, onCompiledQueryChange, onEditModeToggle,
  videoMenuOpen, setVideoMenuOpen, videoSource, quotaBar, costPreview, publicVideoId,
  canDownload, downloadingPillIds, inputCompact,
  streamPillIdRef, uploadingPillIdsRef, threadAreaRef, threadBottomRef,
  inputBarContainerRef, fileInputRef, lastStreamResultRef,
  handleSubmit, handleFileSelected, handleDeleteResult, handleDownloadPill, handleRerunStream,
  attachRenderedVideoForced, attachStreamRecordingUrlForced,
  handleSelectLiveStreams, handleSelectUserVideo, handleSelectDemoVideo, handleSignOut,
  planTier, userName, userInitials,
  onOpenHistory, historyCount,
}: MobileLayoutProps) {
  const streamCtx = useStreamContext()
  const streamSession = streamCtx
  const { isStreaming, setIsStreaming, resultPills, setResultPills, samEnabled, processOffline, setProcessOffline, sourceMode, frozenFrameUrl, activePrompt, promptSent } = streamCtx
  const { pageState } = useInferenceContext()
  const { demoVideos, regularUserVideos, streamUserVideos, clearSelection, deleteVideo } = useVideoLibraryContext()
  const { isSignedIn, isPro, getToken } = usePrimateAuth()

  // P3: session history overlay
  const [historyOpen, setHistoryOpen] = useState(false)

  // PRI-494 (2026-07-29, Matt: "Input covers menu bar"): the input zone is
  // auto-height. Publish its measured height as --pv2-input-h on <html> so the
  // results frame (Zone 2) reserves exactly enough space. ResizeObserver here
  // watches the INPUT BAR only (cheap, low-frequency) — the camera zone remains
  // pure-CSS per the P1 invariant.
  useEffect(() => {
    const el = inputBarContainerRef.current
    if (!el || typeof document === 'undefined') return
    const root = document.documentElement
    const publish = () => {
      const h = el.offsetHeight
      if (h > 0) root.style.setProperty('--pv2-input-h', `${h}px`)
    }
    publish()
    let ro: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(publish)
      ro.observe(el)
    }
    return () => {
      ro?.disconnect()
      root.style.removeProperty('--pv2-input-h')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const computedHistoryCount = computeHistoryCount(resultPills, streamUserVideos, regularUserVideos)

  // P4: new-result chip — shown when a new non-live pill arrives while user is scrolled down
  const [showNewResultChip, setShowNewResultChip] = useState(false)
  const prevNonLivePillCountRef = useRef(0)

  useEffect(() => {
    const nonLiveCount = resultPills.filter(p => !(isLiveStreamActive && p.id === streamPillIdRef.current)).length
    if (nonLiveCount > prevNonLivePillCountRef.current) {
      const frame = threadAreaRef.current
      if (frame && frame.scrollTop > 40) {
        setShowNewResultChip(true)
      }
    }
    prevNonLivePillCountRef.current = nonLiveCount
  }, [resultPills.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // Dismiss chip when user scrolls back to top
  useEffect(() => {
    const frame = threadAreaRef.current
    if (!frame) return
    const onScroll = () => {
      if (frame.scrollTop < 40) setShowNewResultChip(false)
    }
    frame.addEventListener('scroll', onScroll, { passive: true })
    return () => frame.removeEventListener('scroll', onScroll)
  }) // re-attach every render so ref is always fresh

  const handleChipTap = useCallback(() => {
    const frame = threadAreaRef.current
    if (frame) frame.scrollTo({ top: 0, behavior: 'smooth' })
    setShowNewResultChip(false)
    trackMobileNewResultChipTapped()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // PRI-328: camera is active whenever there is a local stream and source is webcam.
  // Intentionally NOT gated by showCarousel — the camera must stay visible even after
  // results render so the user can still aim the camera (3-zone layout spec).
  // Hide only when the full-screen MobileStreamingHUD is open (it has its own camera view).
  const isCameraActive = !!streamSession.localStream && sourceMode === 'webcam' && !showMobileHUD

  // isLiveStreamActive: camera is active AND a stream pill is being created/streamed.
  // Used to render the sticky live card inside the results frame.
  const isLiveStreamActive = isCameraActive && isStreaming && !!streamPillIdRef.current

  // PRI-498: reset thread-area scrollTop whenever the frame geometry changes.
  // (a) Camera activation: thread-area.top grows by camera height — stale scrollTop
  //     leaves a blank band at the top of results.
  // (b) Carousel dismissal: EmptyStateDemo unmounts and result pills take over — any
  //     scrollTop accumulated during the demo (iOS focus-scroll) hides early content.
  useEffect(() => {
    if (threadAreaRef.current) threadAreaRef.current.scrollTop = 0
  }, [isCameraActive]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (carouselDismissed && threadAreaRef.current) threadAreaRef.current.scrollTop = 0
  }, [carouselDismissed]) // eslint-disable-line react-hooks/exhaustive-deps

  // Onboarding exit-on-menu (2026-06-11): when the user opens the More menu while the
  // onboarding demo is playing, permanently exit onboarding.
  useEffect(() => {
    if (moreOpen && showCarousel && !carouselDismissed) {
      setCarouselDismissed(true)
    }
  }, [moreOpen, showCarousel, carouselDismissed, setCarouselDismissed])

  // PRI-328 (iOS Safari hardening): lock document scroll whenever the 3-zone layout is
  // active. On iOS Safari, focusing an input can cause the page to scroll the focused
  // element into view, which drags `position: fixed` children with it during the transition
  // (long-standing WebKit quirk). With document scroll locked, the only scrollable element
  // is the results frame, and the camera zone stays anchored to the visual viewport.
  // Also locks the iOS rubber-band overscroll.
  useEffect(() => {
    if (typeof document === 'undefined') return
    if (showMobileHUD) return // HUD has its own fullscreen handling
    const html = document.documentElement
    const body = document.body
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyOverscroll: (body.style as CSSStyleDeclaration & { overscrollBehavior?: string }).overscrollBehavior ?? '',
      bodyTouchAction: body.style.touchAction,
      bodyWidth: body.style.width,
      bodyHeight: body.style.height,
    }
    html.style.overflow = 'hidden'
    body.style.overflow = 'hidden'
    body.style.position = 'fixed'
    body.style.width = '100%'
    body.style.height = '100%'
    ;(body.style as CSSStyleDeclaration & { overscrollBehavior?: string }).overscrollBehavior = 'none'
    body.style.touchAction = 'manipulation'
    return () => {
      html.style.overflow = prev.htmlOverflow
      body.style.overflow = prev.bodyOverflow
      body.style.position = prev.bodyPosition
      body.style.width = prev.bodyWidth
      body.style.height = prev.bodyHeight
      ;(body.style as CSSStyleDeclaration & { overscrollBehavior?: string }).overscrollBehavior = prev.bodyOverscroll
      body.style.touchAction = prev.bodyTouchAction
    }
  }, [showMobileHUD])

  // Camera permission-denied: webcam mode is active (streaming attempted) but
  // localStream is null AND connectionState is 'error' — camera acquisition failed.
  const cameraPermissionDenied = sourceMode === 'webcam' && isStreaming && !streamSession.localStream && streamSession.connectionState === 'error'

  // Stop handler (shared between pinned live card and thread-area pill)
  function handleStop() {
    const rs = streamSession.remoteStream
    if (rs) {
      const track = rs.getVideoTracks()[0]
      if (track) {
        try {
          const cap = new (window as unknown as { ImageCapture: new (t: MediaStreamTrack) => { grabFrame: () => Promise<ImageBitmap> } }).ImageCapture(track)
          cap.grabFrame().then((bm: ImageBitmap) => {
            const cv = document.createElement('canvas')
            cv.width = bm.width; cv.height = bm.height
            const ctx = cv.getContext('2d')
            if (ctx) {
              if (!streamSession.isFileStream && streamSession.isFrontCamera) { ctx.translate(bm.width, 0); ctx.scale(-1, 1) }
              ctx.drawImage(bm, 0, 0)
            }
            const dataUrl = cv.toDataURL('image/jpeg', 0.8)
            setResultPills(prev => prev.map(p => p.id === streamPillIdRef.current ? { ...p, videoThumb: dataUrl } : p))
          }).catch(() => {})
        } catch {}
      }
    }
    streamSession.stopStream()
    setIsStreaming(false)
  }

  return (
    <>
    {showTermsForAction && <TermsModal onAccept={handleTermsAccepted} />}
    {showConversionDialog && conversionFile && (
      <ConversionDialog file={conversionFile} onConfirm={handleConversionConfirm} onCancel={handleConversionCancel} />
    )}
    <UpgradeModal isOpen={showUpgradeForDownload} onClose={() => setShowUpgradeForDownload(false)} reason="Download session results" />
    <style>{`
      @keyframes pv2-fade-up { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
      @keyframes pv2-highlight-pulse {
        0%, 100% { opacity: 0.7; }
        50%      { opacity: 1; }
      }
      @keyframes pv2-highlight-fade-in {
        from { opacity: 0; }
        to   { opacity: 1; }
      }
      * { box-sizing: border-box; }
      textarea { box-sizing: border-box; }
    `}</style>
    <input ref={fileInputRef} type="file" accept="video/*,.mp4,.mov,.avi,.webm,.mkv" style={{ display: 'none' }}
      onChange={e => { const f = e.target.files?.[0]; if (!f) return; setResultPills([]); void handleFileSelected(f); e.target.value = ''; }}
    />

    {/* More panel (replaces hamburger sidebar on mobile) */}
    <MobileMorePanel
      open={moreOpen}
      onClose={() => setMoreOpen(false)}
      isSignedIn={isSignedIn}
      userInitials={userInitials}
      userName={userName ?? undefined}
      isPro={isPro}
      planTier={planTier}
      demoVideos={demoVideos.map(v => ({ id: v.id, display_name: v.display_name }))}
      userVideos={regularUserVideos.map(v => ({ id: v.id, display_name: v.display_name, thumbnail_url: v.thumbnail_data_url ?? undefined }))}
      streamSessionCount={streamUserVideos.length}
      onLiveStreamsSelect={handleSelectLiveStreams}
      onUserVideoSelect={handleSelectUserVideo}
      onDemoVideoSelect={handleSelectDemoVideo}
      onSignOut={handleSignOut}
    />

    {showMobileHUD && (
      <ErrorBoundary fallback={<div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}><span style={{ color: '#fff', fontSize: 14 }}>Stream HUD unavailable — refresh to continue</span></div>}>
      <MobileStreamingHUD
        remoteStream={streamSession.remoteStream}
        connectionState={streamSession.connectionState}
        metrics={streamSession.metrics}
        activePrompt={activePrompt}
        onPromptUpdate={(p) => {
          trackStreamPromptUpdated({ prompt: p, user_plan: planTier });
          streamSession.sendPromptUpdate(p);
        }}
        promptUpdateError={streamSession.promptUpdateError ?? null}
        isRecalculating={streamSession.connectionState === 'recalculating'}
        onStop={() => {
          streamSession.stopStream();
          setIsStreaming(false);
        }}
        queue={streamSession.queue}
        stopReason={streamSession.stopReason ?? null}
        narrative={resultPills.find(p => p.id === streamPillIdRef.current)?.narrative ?? []}
        localStream={streamSession.localStream}
        isFrontCamera={streamSession.isFrontCamera}
        onMinimize={() => setMobileHUDOpen(false)}
        samEnabled={samEnabled}
        isFileStream={streamSession.isFileStream}
      />
      </ErrorBoundary>
    )}

    {/* ─── HEADER (Zone 0): 44px fixed bar ─────────────────────────────────────
        Contains: Darwin logo + wordmark, optional history icon, account avatar.
        position:fixed + translateZ(0) keeps it on a compositor layer (iOS jitter). */}
    <header
      role="banner"
      data-testid="mobile-header"
      style={{
        position: 'fixed',
        top: 'calc(env(safe-area-inset-top, 0px) + var(--pv2-vv-offset, 0px))',
        left: 0, right: 0,
        height: MOBILE_ZONES.header,
        zIndex: 50,
        background: L.bgPrimary,
        borderBottom: `1px solid ${L.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 12px',
        transform: 'translateZ(0)',
        willChange: 'transform',
        WebkitBackfaceVisibility: 'hidden',
      }}
    >
      {/* Logo + wordmark */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <img src="/Darwin_v3_vinyl_transparent.png" alt="" style={{ height: 28, width: 'auto', objectFit: 'contain', flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: L.textPrimary, letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
          Primate<span style={{ opacity: 0.4, fontWeight: 400 }}> / </span>Intelligence
        </span>
      </div>
      {/* Right actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
        {/* History button — P3: always rendered (wired internally). onOpenHistory prop allows external override. */}
        {(() => {
          const badgeCount = historyCount ?? computedHistoryCount
          const openHistory = onOpenHistory ?? (() => setHistoryOpen(true))
          return (
            <button
              aria-label={`Session history${badgeCount ? ` (${badgeCount} sessions)` : ''}`}
              data-testid="history-icon-btn"
              onClick={openHistory}
              style={{
                position: 'relative', width: 44, height: 44,
                background: 'none', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: L.textMuted, borderRadius: 8, padding: 0,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                <circle cx="9" cy="9" r="7.5" stroke="currentColor" strokeWidth="1.4"/>
                <path d="M9 5v4l2.5 2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {badgeCount > 0 && (
                <span aria-hidden="true" style={{
                  position: 'absolute', top: 7, right: 7,
                  minWidth: 14, height: 14, borderRadius: 7,
                  background: L.signal, color: '#fff',
                  fontSize: 8, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '0 2px', lineHeight: 1,
                }}>
                  {badgeCount > 99 ? '99+' : badgeCount}
                </span>
              )}
            </button>
          )
        })()}
        {/* Account avatar — opens More panel */}
        <button
          aria-label={`Account menu${userName ? ` (${userName})` : ''}`}
          onClick={() => setMoreOpen(true)}
          style={{
            width: 44, height: 44,
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 8, padding: 0,
          }}
        >
          <div style={{
            width: 28, height: 28, borderRadius: 14,
            background: L.signal, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700, letterSpacing: '0.02em',
            userSelect: 'none',
          }}>{userInitials || '?'}</div>
        </button>
      </div>
    </header>

    {/* ─── ZONE 1: Camera viewfinder (fixed, deterministic height) ─────────────
        Height: min(56.25vw, 30vh) — pure CSS, no ResizeObserver, no JS state.
        On 390×844: min(219px, 253px) = 219px.  On 375×667: min(211px, 200px) = 200px.
        position:fixed + translateZ(0) forces a compositor layer — defeats iOS Safari's
        keyboard-animation scroll jitter on fixed children.
        Renders only when camera is active (webcam mode with localStream) OR
        file mode with frozenFrameUrl/active file stream. */}
    {(isCameraActive || cameraPermissionDenied || (sourceMode === 'file' && frozenFrameUrl)) && (
      <div
        data-testid="mobile-camera-block"
        aria-label="Camera viewfinder"
        style={{
          position: 'fixed',
          top: `calc(env(safe-area-inset-top, 0px) + var(--pv2-vv-offset, 0px) + ${MOBILE_ZONES.header}px)`,
          left: 0, right: 0,
          height: mobileCameraHeightCss(),
          zIndex: 40,
          background: '#09090D',
          transform: 'translateZ(0)',
          willChange: 'transform',
          WebkitBackfaceVisibility: 'hidden',
          overflow: 'hidden',
        }}
      >
        {/* Camera permission-denied state: webcam attempted but camera access failed */}
        {cameraPermissionDenied ? (
          <div
            data-testid="camera-permission-denied"
            style={{
              width: '100%', height: '100%',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              gap: 12, padding: '16px 20px', background: '#09090D',
            }}
          >
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
              <path d="M4 4l24 24" stroke="#666" strokeWidth="2" strokeLinecap="round"/>
              <path d="M8.5 8H6a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 001.6-.8" stroke="#666" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M11 8h9a2 2 0 012 2v7M22 22H6" stroke="#666" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
            <p style={{ color: '#aaa', fontSize: 12, textAlign: 'center', margin: 0, lineHeight: 1.5 }}>
              Camera access is off — enable it in Settings ▸ Safari
            </p>
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{
                background: L.signal, color: '#fff',
                border: 'none', borderRadius: 8,
                padding: '10px 18px', fontSize: 13,
                cursor: 'pointer', minHeight: 44, minWidth: 44,
              }}
            >
              Upload a video instead
            </button>
          </div>
        ) : isCameraActive ? (
          <div data-testid="mobile-webcam-camera" style={{ width: '100%', height: '100%' }}>
            <MobileCameraPreview stream={streamSession.localStream!} isFrontCamera={streamSession.isFrontCamera} />
          </div>
        ) : (
          /* File mode: show frozen frame */
          <div style={{ width: '100%', height: '100%', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {frozenFrameUrl && (
              <img src={frozenFrameUrl} alt="Video frame" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            )}
          </div>
        )}
      </div>
    )}

    {/* ─── ZONE 2: Results frame (fixed, internal scroll only) ─────────────────
        Single container spanning from camera-bottom to input-top.
        document never scrolls — only THIS element scrolls internally.
        Live (streaming) card is a sticky child at top:0 inside this frame.
        body scroll-lock (above) + overscrollBehavior:contain prevent any bleed-through. */}
    <div
      role="main"
      aria-label="Results"
      data-testid="thread-area"
      ref={threadAreaRef}
      style={{
        position: 'fixed',
        top: (isCameraActive || cameraPermissionDenied || (sourceMode === 'file' && frozenFrameUrl))
          ? `calc(env(safe-area-inset-top, 0px) + var(--pv2-vv-offset, 0px) + ${MOBILE_ZONES.header}px + ${mobileCameraHeightCss()})`
          : `calc(env(safe-area-inset-top, 0px) + var(--pv2-vv-offset, 0px) + ${MOBILE_ZONES.header}px)`,
        left: 0, right: 0,
        bottom: `calc(env(safe-area-inset-bottom, 0px) + ${MOBILE_ZONES.tabBar}px + var(--pv2-input-h, ${MOBILE_ZONES.input}px) + var(--keyboard-offset, 0px))`,
        background: L.bgPrimary,
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {/* Live card — sticky at top of results frame while stream is active.
          Replaces the old separate fixed "mobile-active-pill-block" layer.
          position:sticky + top:0 keeps it pinned during internal scroll without
          fighting the document (which is scroll-locked). */}
      {isLiveStreamActive && (() => {
        const streamPill = resultPills.find(p => p.id === streamPillIdRef.current)
        if (!streamPill) return null
        return (
          <div
            data-testid="live-card-sticky"
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              background: L.bgPrimary,
              borderBottom: `1px solid ${L.border}`,
              height: 'clamp(180px, 35vh, 280px)',
              overflow: 'hidden',
            }}
          >
            <ResultPillComp
              pill={streamPill}
              isMobile
              pinnedMode
              connectionState={streamSession.connectionState}
              errorMessage={streamSession.errorMessage}
              remoteStream={streamSession.remoteStream}
              localStream={streamSession.localStream}
              isFileStream={streamSession.isFileStream}
              isFrontCamera={streamSession.isFrontCamera}
              isSaving={uploadingPillIdsRef.current.has(streamPill.id)}
              onDeleteResult={() => handleDeleteResult(streamPill)}
              onDownload={() => handleDownloadPill(streamPill)}
              isDownloading={downloadingPillIds.has(streamPill.id)}
              canDownload={canDownload}
              isStreamActive
              onExpandFullscreen={undefined /* HUD disabled 2026-05-27 */}
              planTier={planTier}
              onStop={handleStop}
            />
          </div>
        )
      })()}

      {/* Scrollable content inside the results frame */}
      <div style={{
        padding: '16px 12px calc(16px)',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        gap: 16,
      }}>
        {/* Onboarding v3 (2026-05-23, dev only): EmptyStateDemo replaces the
            reverted DemoReelPlayer on mobile. Unmounts when carouselDismissed flips true.
            Also unmounts whenever the More menu is open (2026-06-11): the demo's
            OnboardingHighlights overlay is position:fixed z-index 9999 and would
            otherwise paint OVER the menu sheet. */}
        {showCarousel && !carouselDismissed && !isStreaming && !moreOpen && (
          <div
            data-testid="empty-state-demo-slot"
            style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
          >
            <EmptyStateDemo
              isMobile
              onTryForYourself={() => {
                setCarouselDismissed(true)
                setVideoMenuOpen(true)
              }}
              onUserTakeover={() => setCarouselDismissed(true)}
              isSignedIn={!!isSignedIn}
              onPersistentDismiss={() => setCarouselDismissed(true)}
            />
          </div>
        )}

        {/* Progress trays - inline on mobile */}
        {showProgress && (
          <ProgressBlock step={processingStep} processingText={processingText} isMobile samEnabled={samEnabled} jobProgress={jobProgress} completingSteps={completingSteps} dismissing={offlineProgressDismissing} />
        )}
        {showStreamProgress && (
          <StreamProgressTray
            connectionState={streamSession.connectionState}
            prompt={activePrompt}
            queue={streamSession.queue}
            isMobile
            dismissing={streamProgressDismissing}
            connectProgress={streamSession.connectProgress}
            onCancel={() => {
              streamSession.cancelPreConnect()
              setIsStreaming(false)
            }}
          />
        )}
        {/* Stream error narrative (mobile) */}
        {streamSession.connectionState === 'error' && (
          <StreamErrorTray
            errorMessage={streamSession.errorMessage ?? null}
            isMobile
            onDismiss={() => {
              setIsStreaming(false)
              streamSession.reset()
            }}
          />
        )}

        {/* Result pills — filter out the active live pill (rendered sticky above) */}
        {resultPills
          .filter(pill => !(pill.id === streamPillIdRef.current && isLiveStreamActive))
          .map((pill, idx, arr) => (
            <div key={pill.id} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              <ResultPillComp
                pill={pill}
                isMobile
                isNewest={idx === arr.length - 1}
                connectionState={streamSession.connectionState}
                errorMessage={pill.id === streamPillIdRef.current ? streamSession.errorMessage : null}
                remoteStream={pill.id === streamPillIdRef.current ? streamSession.remoteStream : null}
                localStream={pill.id === streamPillIdRef.current ? streamSession.localStream : null}
                isFileStream={pill.id === streamPillIdRef.current && streamSession.isFileStream}
                isFrontCamera={streamSession.isFrontCamera}
                onPlayVideo={
                  (!pill.id.startsWith('stream-') && !pill.videoUrl)
                    ? () => attachRenderedVideoForced(pill.id)
                  : (pill.id.startsWith('stream-') && !pill.videoUrl && !!pill.recordingUrl && pill.id !== streamPillIdRef.current)
                    ? () => attachStreamRecordingUrlForced(pill.id, pill.recordingUrl!)
                  : undefined
                }
                onRerunStream={pill.id.startsWith('stream-') && pill.id !== streamPillIdRef.current && !pill.videoUrl && !pill.recordingUrl && !uploadingPillIdsRef.current.has(pill.id) ? handleRerunStream : undefined}
                isSaving={uploadingPillIdsRef.current.has(pill.id)}
                onDeleteResult={() => handleDeleteResult(pill)}
                onDownload={() => handleDownloadPill(pill)}
                isDownloading={downloadingPillIds.has(pill.id)}
                canDownload={canDownload}
                isStreamActive={(pill.id === streamPillIdRef.current && isStreaming) || (pill.id.startsWith('offline-') && offlineJobActive)}
                onExpandFullscreen={undefined /* HUD disabled 2026-05-27 */}
                planTier={planTier}
                onStop={pill.id.startsWith('stream-') && pill.id === streamPillIdRef.current ? handleStop : undefined}
              />
            </div>
          ))}
        <div ref={threadBottomRef} />
      </div>
    </div>

    {/* ─── ZONE 3: Input bar (auto-height, anchored above tab bar) ────────────
        PRI-494 fix (2026-07-29, Matt: "Input covers menu bar"): the previous
        hard height:68px + alignItems:center made the ~132px-tall composer
        overflow symmetrically — the bottom half painted OVER the tab bar
        (zIndex 1010 > 1000). The zone is now auto-height: bottom stays anchored
        above the tab bar, growth goes UPWARD, and the measured height is
        published as --pv2-input-h (see effect above) so the results frame
        shrinks to match. Lifts via --keyboard-offset when the soft keyboard is
        open. Hidden while auth is hydrating and while the EmptyStateDemo owns
        the screen. */}
    <div
      ref={inputBarContainerRef}
      data-testid="mobile-input-bar-fixed"
      role="form"
      aria-label="Prompt input"
      style={{
        position: 'fixed',
        bottom: `calc(${MOBILE_ZONES.tabBar}px + env(safe-area-inset-bottom, 0px) + var(--keyboard-offset, 0px))`,
        left: 0, right: 0,
        // zIndex must exceed MobileTabBar (1000) so InputBar popovers (video
        // source menu) win hit-testing at the bottom boundary. The zone itself
        // no longer overlaps the tab bar (auto-height, anchored above). (PRI-494)
        zIndex: 1010,
        background: L.bgPrimary,
        borderTop: `1px solid ${L.border}`,
        display:
          !authLoaded || (showCarousel && !carouselDismissed)
            ? 'none'
            : 'block',
      }}
    >
      <div style={{ padding: '8px 12px', width: '100%', boxSizing: 'border-box' }}>
        {videoMenuOpen && (
          <VideoSourcePopover
            isMobile
            onClose={() => setVideoMenuOpen(false)}
            onUpload={() => fileInputRef.current?.click()}
            onStream={(deviceId?: string) => {
              streamCtx.setSourceMode('webcam');
              if (deviceId) streamSession.selectDevice(deviceId);
              setIsStreaming(true);
              lastStreamResultRef.current = null;
              setVideoMenuOpen(false);
              void streamSession.requestCameraAccess();
              if (getToken) void getToken().then(t => streamSession.prewarmTurn(t)).catch(() => {});
            }}
            devices={streamSession.devices}
            selectedDeviceId={streamSession.selectedDeviceId}
            onSelectDevice={streamSession.selectDevice}
          />
        )}
        <InputBar
          isMobile
          compact={inputCompact}
          videoSource={videoSource}
          sourceModeOverride={sourceMode}
          onSourceModeChange={streamCtx.setSourceMode}
          onSwitchToWebcam={() => {
            clearSelection();
            streamSession.reacquireCamera();
          }}
          onSwitchToFile={() => { streamSession.cancelPreConnect(); streamSession.releaseCamera(); }}
          onClearVideo={() => {
            if (isStreaming) { setIsStreaming(false); streamSession.stopStream(); }
            streamSession.releaseCamera();
            clearSelection();
            setResultPills([]);
          }}
          prompt={prompt}
          onPromptChange={setPrompt}
          onSubmit={handleSubmit}
          onOpenVideoMenu={() => setVideoMenuOpen(v => !v)}
          onRequestFile={() => fileInputRef.current?.click()}
          processOffline={processOffline}
          onProcessOfflineChange={setProcessOffline}
          disabled={pageState === 'processing' || pageState === 'uploading'}
          localStream={streamSession.localStream}
          frozenFrameUrl={frozenFrameUrl}
          quotaBar={quotaBar}
          costPreview={costPreview}
          publicVideoId={publicVideoId}
          streamingMetering={isStreaming ? streamSession.metering : null}
          isStreaming={isStreaming}
          promptSent={promptSent}
          hasResultPills={resultPills.length > 0}
          isFileStream={streamSession.isFileStream}
          fileDuration={streamSession.fileDuration}
          fileCurrentTime={streamSession.fileCurrentTime}
          cameraDevices={streamSession.devices}
          selectedCameraId={streamSession.selectedDeviceId}
          onSelectCamera={streamSession.selectDevice}
          hideVideoChip={isStreaming || sourceMode === 'file'}
          isFrontCamera={streamSession.isFrontCamera}
          compiledQuery={compiledQuery}
          parseState={parseState}
          parseError={queryError}
          onCompiledQueryChange={onCompiledQueryChange}
          onEditModeToggle={onEditModeToggle}
          planTier={planTier}
          stablePromptPreview
          frameless
        />
      </div>
    </div>
    {/* P4: new-result chip — floats near top of results frame when user is scrolled down */}
    {showNewResultChip && (
      <div
        aria-live="polite"
        style={{
          position: 'fixed',
          top: (isCameraActive || cameraPermissionDenied || (sourceMode === 'file' && frozenFrameUrl))
            ? `calc(env(safe-area-inset-top, 0px) + var(--pv2-vv-offset, 0px) + ${MOBILE_ZONES.header}px + ${mobileCameraHeightCss()} + 10px)`
            : `calc(env(safe-area-inset-top, 0px) + var(--pv2-vv-offset, 0px) + ${MOBILE_ZONES.header}px + 10px)`,
          left: 0, right: 0,
          display: 'flex', justifyContent: 'center',
          zIndex: 45,
          pointerEvents: 'none',
        }}
      >
        <button
          data-testid="new-result-chip"
          onClick={handleChipTap}
          style={{
            pointerEvents: 'auto',
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '0 16px',
            height: 44, minWidth: 44,
            background: L.signal,
            color: '#fff',
            border: 'none',
            borderRadius: 22,
            fontSize: 13, fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M6 10V2M2 6l4-4 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          New result
        </button>
      </div>
    )}

    {!showMobileHUD && <MobileTabBar onOpenMore={() => setMoreOpen(prev => !prev)} moreActive={moreOpen} />}

    {/* P3: Session History overlay */}
    <MobileSessionHistory
      open={historyOpen}
      onClose={() => setHistoryOpen(false)}
      isStreaming={isStreaming}
      streamPillIdRef={streamPillIdRef}
      resultPills={resultPills}
      streamUserVideos={streamUserVideos}
      regularUserVideos={regularUserVideos}
      handleDeleteResult={handleDeleteResult}
      deleteVideo={deleteVideo}
      handleSelectUserVideo={handleSelectUserVideo}
      attachRenderedVideoForced={attachRenderedVideoForced}
      setVideoMenuOpen={setVideoMenuOpen}
    />
    </>
  )
}
